import request from "@/utils/request";
import { PERSONAL_DOCUMENT_MAX_FILE_COUNT } from "@/utils/personalDocumentFiles";

export type PersonalRagStatus = "queued" | "running" | "succeeded" | "failed" | null;

export interface PersonalDocument {
  id: string;
  documentId: string;
  fileName: string;
  fileSize: number;
  fileExtension: string;
  uploadStatus: string | null;
  ragJobId: string | null;
  ragJobStatus: PersonalRagStatus;
  ragJobMessage: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface PersonalDocumentSearchParams {
  search?: string;
  status?: string;
  sortBy?: "createdAt" | "fileName" | "size" | "status";
  sortDescending?: boolean;
  page?: number;
  pageSize?: number;
}

export interface PersonalDocumentSearchResult {
  items: PersonalDocument[];
  total: number;
  totalFileCount: number;
  maxFileCount: number;
  page: number;
  pageSize: number;
}

export interface PersonalDocumentStatus {
  documentListId: string;
  uploadStatus: string | null;
  ragJobId: string | null;
  ragJobStatus: PersonalRagStatus;
  ragJobMessage: string | null;
}

export interface PersonalUploadTicket {
  clientId: string;
  documentListId: string;
  documentId: string;
  fileName: string;
  uploadUrl: string;
  expiresAt: string;
  headers: Record<string, string>;
}

export interface PersonalUploadRejected {
  clientId: string;
  fileName: string;
  error: string;
}

export interface PersonalUploadInitiateResult {
  accepted: PersonalUploadTicket[];
  rejected: PersonalUploadRejected[];
}

export interface PersonalDocumentOperationResult {
  documentListId: string;
  success: boolean;
  status: string | null;
  message: string | null;
}

export interface PersonalDocumentRetryResult extends PersonalDocumentOperationResult {
  ragJobId: string | null;
}

const BASE = "/project/personaldocument";

const read = <T = unknown>(value: any, camel: string, pascal: string, fallback?: T): T =>
  (value?.[camel] ?? value?.[pascal] ?? fallback) as T;

function normalizeDocument(value: any): PersonalDocument {
  return {
    id: String(read(value, "id", "Id", read(value, "ID", "ID", ""))),
    documentId: String(read(value, "documentId", "DocumentId", "")),
    fileName: String(read(value, "fileName", "FileName", "")),
    fileSize: Number(read(value, "fileSize", "FileSize", 0)),
    fileExtension: String(read(value, "fileExtension", "FileExtension", "")),
    uploadStatus: read(value, "uploadStatus", "UploadStatus", null),
    ragJobId: read(value, "ragJobId", "RagJobId", null),
    ragJobStatus: read(value, "ragJobStatus", "RagJobStatus", null),
    ragJobMessage: read(value, "ragJobMessage", "RagJobMessage", null),
    createdAt: read(value, "createdAt", "CreatedAt", null),
    updatedAt: read(value, "updatedAt", "UpdatedAt", null),
  };
}

function normalizeStatus(value: any): PersonalDocumentStatus {
  return {
    documentListId: String(read(value, "documentListId", "DocumentListId", "")),
    uploadStatus: read(value, "uploadStatus", "UploadStatus", null),
    ragJobId: read(value, "ragJobId", "RagJobId", null),
    ragJobStatus: read(value, "ragJobStatus", "RagJobStatus", null),
    ragJobMessage: read(value, "ragJobMessage", "RagJobMessage", null),
  };
}

export function getPersonalDocumentStatusPollingIds(documents: PersonalDocument[]): string[] {
  return documents
    .filter(
      (document) =>
        document.uploadStatus === "Pending" ||
        document.ragJobStatus === "queued" ||
        document.ragJobStatus === "running",
    )
    .map((document) => document.id);
}

/**
 * 只替换发生变化的状态对象；状态完全相同时保留数组引用，变化时也保留未变化文档的对象引用。
 * 这样轮询不会重建文件名等列表 DOM。
 */
export function mergePersonalDocumentStatuses(
  documents: PersonalDocument[],
  statuses: PersonalDocumentStatus[],
): PersonalDocument[] {
  const byId = new Map(statuses.map((status) => [status.documentListId, status]));
  let changed = false;
  const next = documents.map((document) => {
    const status = byId.get(document.id);
    if (!status) return document;
    if (
      document.uploadStatus === status.uploadStatus &&
      document.ragJobId === status.ragJobId &&
      document.ragJobStatus === status.ragJobStatus &&
      document.ragJobMessage === status.ragJobMessage
    ) {
      return document;
    }
    changed = true;
    return {
      ...document,
      uploadStatus: status.uploadStatus,
      ragJobId: status.ragJobId,
      ragJobStatus: status.ragJobStatus,
      ragJobMessage: status.ragJobMessage,
    };
  });
  return changed ? next : documents;
}

function normalizeTicket(value: any): PersonalUploadTicket {
  return {
    clientId: String(read(value, "clientId", "ClientId", "")),
    documentListId: String(read(value, "documentListId", "DocumentListId", "")),
    documentId: String(read(value, "documentId", "DocumentId", "")),
    fileName: String(read(value, "fileName", "FileName", "")),
    uploadUrl: String(read(value, "uploadUrl", "UploadUrl", "")),
    expiresAt: String(read(value, "expiresAt", "ExpiresAt", "")),
    headers: read(value, "headers", "Headers", {}) ?? {},
  };
}

export const personalDocumentApi = {
  async search(params: PersonalDocumentSearchParams): Promise<PersonalDocumentSearchResult> {
    const response = await request.get(`${BASE}/search`, {
      params: {
        Search: params.search || undefined,
        Status: params.status || undefined,
        SortBy: params.sortBy ?? "createdAt",
        SortDescending: params.sortDescending ?? true,
        Page: params.page ?? 1,
        PageSize: params.pageSize ?? 100,
      },
    });
    const raw = response.data ?? {};
    const total = Number(read(raw, "total", "Total", 0));
    return {
      items: (read<any[]>(raw, "items", "Items", []) ?? []).map(normalizeDocument),
      total,
      totalFileCount: Number(read(raw, "totalFileCount", "TotalFileCount", total)),
      maxFileCount: Number(
        read(raw, "maxFileCount", "MaxFileCount", PERSONAL_DOCUMENT_MAX_FILE_COUNT),
      ),
      page: Number(read(raw, "page", "Page", 1)),
      pageSize: Number(read(raw, "pageSize", "PageSize", 100)),
    };
  },

  async statuses(documentListIds: string[]): Promise<PersonalDocumentStatus[]> {
    const ids = [...new Set(documentListIds.filter(Boolean))];
    const statuses: PersonalDocumentStatus[] = [];
    for (let index = 0; index < ids.length; index += 200) {
      const response = await request.post(`${BASE}/statuses`, {
        DocumentListIds: ids.slice(index, index + 200),
      });
      const raw = Array.isArray(response.data) ? response.data : [];
      statuses.push(...raw.map(normalizeStatus));
    }
    return statuses;
  },

  async initiate(files: Array<{ clientId: string; file: File }>) {
    const response = await request.post(`${BASE}/uploads/initiate`, {
      Files: files.map(({ clientId, file }) => ({
        ClientId: clientId,
        FileName: file.name,
        Size: file.size,
        ContentType: file.type || "application/octet-stream",
      })),
    });
    const raw = response.data ?? {};
    return {
      accepted: (read<any[]>(raw, "accepted", "Accepted", []) ?? []).map(normalizeTicket),
      rejected: (read<any[]>(raw, "rejected", "Rejected", []) ?? []).map((item) => ({
        clientId: String(read(item, "clientId", "ClientId", "")),
        fileName: String(read(item, "fileName", "FileName", "")),
        error: String(read(item, "error", "Error", "Upload rejected")),
      })),
    } satisfies PersonalUploadInitiateResult;
  },

  async renew(documentListId: string): Promise<PersonalUploadTicket> {
    const response = await request.post(`${BASE}/uploads/${documentListId}/renew`);
    return normalizeTicket(response.data);
  },

  async complete(documentListId: string, etag?: string | null) {
    return request
      .post(`${BASE}/uploads/${documentListId}/complete`, { ETag: etag || undefined })
      .then((response) => response.data);
  },

  async deleteBatch(documentListIds: string[]) {
    const response = await request.post(`${BASE}/BatchDelete`, {
      DocumentListIds: documentListIds,
    });
    return normalizeOperations(response.data);
  },

  async retry(documentListId: string): Promise<PersonalDocumentRetryResult> {
    const response = await request.post(`${BASE}/${documentListId}/retry`);
    const raw = response.data ?? {};
    return {
      documentListId: String(read(raw, "documentListId", "DocumentListId", documentListId)),
      success: Boolean(read(raw, "success", "Success", false)),
      status: read(raw, "status", "Status", null),
      message: read(raw, "message", "Message", null),
      ragJobId: read(raw, "ragJobId", "RagJobId", null),
    };
  },

  async download(documentListId: string) {
    const popup = window.open("about:blank", "_blank");
    try {
      const response = await request.get(`${BASE}/${documentListId}/download-url`);
      const url = read<string>(response.data, "url", "Url", "");
      if (!url) throw new Error("Download URL is unavailable");
      if (popup) {
        popup.opener = null;
        popup.location.href = url;
      } else {
        window.location.assign(url);
      }
    } catch (error) {
      popup?.close();
      throw error;
    }
  },
};

function normalizeOperations(value: any): PersonalDocumentOperationResult[] {
  if (!Array.isArray(value)) return [];
  return value.map((item) => {
    const status = read<string | null>(item, "status", "Status", null);
    const explicitSuccess = read<boolean | undefined>(item, "success", "Success", undefined);
    return {
      documentListId: String(read(item, "documentListId", "DocumentListId", "")),
      success:
        explicitSuccess ??
        !String(status ?? "")
          .toLowerCase()
          .includes("failed"),
      status,
      message: read(item, "message", "Message", null),
    };
  });
}
